import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebase/config";
import { doc, onSnapshot, updateDoc, arrayUnion, deleteDoc } from "firebase/firestore";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { createBlankReport } from "../data/BOOK_REPORT_STRUCTURE";
import SidekickBanner from "../components/SidekickBanner";

export default function BookDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const theme = useTheme();
  const isChild = userProfile?.role === "child";
  const [book, setBook] = useState(null);
  const [endPage, setEndPage] = useState("");
  const [loading, setLoading] = useState(false);
  const [editingDate, setEditingDate] = useState(false);
  const [newTargetDate, setNewTargetDate] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // Tracks which collapsible cards are open. Log Session defaults open
  // since logging a reading session is the most frequent action on this page.
  const [openSections, setOpenSections] = useState({ logSession: true });
  function toggleSection(key) {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, "books", id), (snap) => {
      if (snap.exists()) setBook({ id: snap.id, ...snap.data() });
    });
    return unsubscribe;
  }, [id]);

  function getStats() {
    if (!book) return {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const pagesRead = book.pagesRead || 0;
    const remaining = book.totalPages - pagesRead;
    const percentComplete = Math.round((pagesRead / book.totalPages) * 100);

    let daysLeft = null;
    let pagesPerDay = null;
    let projectedDate = null;
    let avgPages = null;

    if (book.targetDate) {
      const target = new Date(book.targetDate);
      target.setHours(0, 0, 0, 0);
      daysLeft = Math.ceil((target - today) / (1000 * 60 * 60 * 24));
      pagesPerDay = daysLeft > 0 ? Math.ceil(remaining / daysLeft) : null;
    }

    if (book.sessions && book.sessions.length > 0) {
      avgPages = Math.round(pagesRead / book.sessions.length);
      if (!book.targetDate && avgPages > 0) {
        const daysToFinish = Math.ceil(remaining / avgPages);
        projectedDate = new Date();
        projectedDate.setDate(projectedDate.getDate() + daysToFinish);
      }
    }

    return { pagesRead, remaining, percentComplete, daysLeft, pagesPerDay, projectedDate, avgPages };
  }

  function getQuizStatus(quiz) {
    const currentPage = book?.currentPage || 0;
    const milestoneReached = currentPage >= quiz.milestone;

    // needsRevision takes top priority — parent sent it back, student must act
    if (quiz.needsRevision) {
      return { state: "revise" };
    }

    if (quiz.completedByChild) {
      // Gate on reviewedByParent — not on auto-detected free-response scoring.
      // This matches Quiz.js's isReviewed logic exactly.
      if (quiz.reviewedByParent) {
        let earned = 0;
        let possible = 0;
        (quiz.questions || []).forEach((q, i) => {
          const ans = (quiz.childAnswers || {})[i];
          if (q.type === "free_response") {
            possible += 10;
            if (quiz.parentScores?.[i] !== undefined) earned += quiz.parentScores[i];
          } else if (q.type === "multi_select") {
            const correct = q.correctAnswers || [];
            possible += correct.length;
            (ans || []).forEach(ci => { if (correct.includes(ci)) earned++; });
          } else {
            possible += 1;
            if (ans === q.correctAnswer) earned++;
          }
        });
        return { state: "reviewed", earned, possible };
      }
      return { state: "completed" };
    }

    if (!milestoneReached) {
      return { state: "upcoming" };
    }

    if (!quiz.unlockedByParent) {
      return { state: "pending" };
    }

    return { state: "unlocked" };
  }

  async function logSession() {
    if (!endPage) return alert("Please enter the page you finished on.");
    const end = parseInt(endPage);
    if (end <= book.currentPage) return alert(`You must enter a page greater than ${book.currentPage}.`);
    if (end > book.totalPages) return alert(`Page cannot exceed total pages (${book.totalPages}).`);

    const pagesThisSession = end - book.currentPage;
    const newPagesRead = (book.pagesRead || 0) + pagesThisSession;

    setLoading(true);
    try {
      await updateDoc(doc(db, "books", id), {
        currentPage: end,
        pagesRead: newPagesRead,
        status: book.status === "queue" ? "active" : book.status,
        sessions: arrayUnion({
          date: new Date().toISOString(),
          startPage: book.currentPage,
          endPage: end,
          pagesRead: pagesThisSession
        })
      });
      setEndPage("");
      alert(`Great job! You read ${pagesThisSession} pages today!`);
    } catch {
      alert("Failed to log session. Try again.");
    }
    setLoading(false);
  }

async function openBookReport() {
  const existingReports = book.bookReports || [];
  if (existingReports.length > 0) {
    const latest = existingReports[existingReports.length - 1];
    navigate(`/book/${id}/report/${latest.id}`);
  } else {
    const newReport = createBlankReport("Draft 1");
    await updateDoc(doc(db, "books", id), { bookReports: [newReport] });
    navigate(`/book/${id}/report/${newReport.id}`);
  }
}

async function promoteToActive() {
    try {
      await updateDoc(doc(db, "books", id), {
        status: "active",
        targetDate: newTargetDate || null,
      });
    } catch {
      alert("Failed to start book. Try again.");
    }
  }

  async function saveTargetDate() {
    try {
      await updateDoc(doc(db, "books", id), {
        targetDate: newTargetDate || null
      });
      setEditingDate(false);
      alert("Target date updated!");
    } catch {
      alert("Failed to update date. Try again.");
    }
  }

  async function deleteBook() {
    try {
      await deleteDoc(doc(db, "books", id));
      navigate(-1);
    } catch {
      alert("Failed to delete book. Try again.");
    }
  }

  if (!book) return <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", backgroundColor: theme.bgPage, color: theme.colorText }}>Loading...</div>;

  const stats = getStats();
  const isParent = userProfile?.role === "parent";
  const isQueue = book.status === "queue";
  const addedByParent = !!book.assignedBy;

  const childCanDelete = !isParent && isQueue && !addedByParent;
  const childBlocked = !isParent && (!isQueue || addedByParent);

  const canDelete = isParent || childCanDelete;

  const quizzes = [...(book.quizzes || [])].sort((a, b) => a.milestone - b.milestone);
  // Student-only alert counts — drive the Phase 3 alert banner and "New" badge
  const reviseAlertCount = !isParent ? quizzes.filter((q) => q.needsRevision).length : 0;
  const newFeedbackCount = !isParent ? quizzes.filter(
    (q) => q.completedByChild && q.reviewedByParent && q.feedbackSeenByChild !== true
  ).length : 0;

const styles = {
    container: { minHeight: "100vh", backgroundColor: theme.bgPage },
    loading: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", backgroundColor: theme.bgPage, color: theme.colorText },
    content: { padding: 24, maxWidth: 600, margin: "0 auto" },
    modalOverlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
    modalBox: { backgroundColor: isChild ? theme.bgCard : "white", borderRadius: 16, padding: 28, maxWidth: 360, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", border: isChild ? `1px solid ${theme.bgCardBorder}` : "none" },
    modalTitle: { fontSize: 20, color: isChild ? theme.colorText : "#2d3748", margin: "0 0 12px 0", fontFamily: theme.fontBody },
    modalText: { fontSize: 15, color: isChild ? theme.colorTextSoft : "#718096", margin: "0 0 24px 0", lineHeight: 1.6, fontFamily: theme.fontBody },
    modalButtons: { display: "flex", gap: 10 },
    modalCancel: { flex: 1, padding: 12, backgroundColor: isChild ? theme.bgPage : "#e2e8f0", color: isChild ? theme.colorText : "#4a5568", border: "none", borderRadius: 8, fontSize: 15, cursor: "pointer", fontWeight: "bold" },
    modalConfirm: { flex: 1, padding: 12, backgroundColor: "#f56565", color: "white", border: "none", borderRadius: 8, fontSize: 15, cursor: "pointer", fontWeight: "bold" },
    card: { backgroundColor: isChild ? theme.bgCard : "white", borderRadius: 12, padding: 24, marginBottom: 16, border: isChild ? `2px solid ${theme.bgCardBorder}` : "none", boxShadow: isChild ? "none" : "0 2px 4px rgba(0,0,0,0.1)" },
    bookHeader: { display: "flex", gap: 16, alignItems: "flex-start" },
    cover: { width: 80, height: 120, objectFit: "cover", borderRadius: 6 },
    bookMeta: { flex: 1 },
    bookTitle: { fontSize: 20, color: theme.colorText, margin: "0 0 8px 0", fontFamily: theme.fontBody, fontWeight: "bold" },
    bookAuthor: { fontSize: 16, color: theme.colorTextSoft, margin: "0 0 8px 0", fontFamily: theme.fontBody },
    bookMetaText: { fontSize: 13, color: theme.colorTextSoft, margin: "0 0 4px 0", fontFamily: theme.fontBody },
    closeBtn: { background: "none", border: "none", fontSize: 18, cursor: "pointer", color: theme.colorTextSoft, fontWeight: "bold", lineHeight: 1, alignSelf: "flex-start", padding: "0 0 0 4px" },
    askParentText: { fontSize: 13, color: theme.colorTextSoft, margin: "12px 0 0 0", paddingTop: 12, borderTop: `1px solid ${theme.bgCardBorder}`, fontFamily: theme.fontBody },
    editDetailsBtn: { width: "100%", marginTop: 14, padding: "10px 0", backgroundColor: isChild ? theme.bgPage : "#f7fafc", color: isChild ? theme.colorText : "#4a5568", border: `1px solid ${theme.bgCardBorder}`, borderRadius: 8, fontSize: 14, cursor: "pointer", fontWeight: "bold", fontFamily: theme.fontBody },
    sectionTitle: { fontSize: 18, color: theme.colorText, marginTop: 0, marginBottom: 16, fontFamily: theme.fontBody, fontWeight: "bold" },
    progressBar: { height: 12, backgroundColor: theme.bgPage, borderRadius: 6, marginBottom: 8, border: `1px solid ${theme.bgCardBorder}` },
    progressFill: { height: "100%", backgroundColor: theme.colorSuccess, borderRadius: 6, transition: "width 0.3s ease" },
    progressText: { fontSize: 14, color: theme.colorTextSoft, marginBottom: 16, fontFamily: theme.fontBody },
    statsGrid: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 16 },
    statBox: { backgroundColor: theme.bgPage, borderRadius: 8, padding: 12, textAlign: "center", border: `1px solid ${theme.bgCardBorder}` },
    statNumber: { fontSize: 22, fontWeight: "bold", color: theme.colorAccent, margin: "0 0 4px 0", fontFamily: theme.fontBody },
    statLabel: { fontSize: 11, color: theme.colorTextSoft, margin: 0, fontFamily: theme.fontBody },
    recastBanner: { backgroundColor: theme.colorPrimary, borderRadius: 8, padding: "12px 16px", marginTop: 8 },
    recastText: { color: "white", fontWeight: "bold", margin: 0, fontSize: 14, fontFamily: theme.fontBody },
    recastSub: { color: "white", opacity: 0.9, margin: "4px 0 0 0", fontSize: 12, fontFamily: theme.fontBody },
    currentPageText: { color: theme.colorTextSoft, marginBottom: 12, fontFamily: theme.fontBody },
    label: { display: "block", fontWeight: "bold", color: theme.colorText, marginBottom: 6, fontSize: 14, fontFamily: theme.fontBody },
    input: { width: "100%", padding: 12, borderRadius: 8, border: `2px solid ${theme.bgCardBorder}`, fontSize: 16, boxSizing: "border-box", marginBottom: 12, backgroundColor: theme.bgPage, color: theme.colorText, fontFamily: theme.fontBody },
    logBtn: { width: "100%", padding: 14, backgroundColor: theme.colorSuccess, color: "white", border: "none", borderRadius: 8, fontSize: 16, cursor: "pointer", fontFamily: theme.fontBody, fontWeight: "bold" },
    sessionRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${theme.bgCardBorder}` },
    sessionDate: { fontWeight: "bold", color: theme.colorText, margin: "0 0 4px 0", fontSize: 14, fontFamily: theme.fontBody },
    sessionPages: { color: theme.colorTextSoft, margin: 0, fontSize: 13, fontFamily: theme.fontBody },
    sessionCount: { color: theme.colorSuccess, fontWeight: "bold", fontSize: 16, fontFamily: theme.fontBody },
    scaffoldBtn: { width: "100%", padding: 16, backgroundColor: theme.colorPrimary, color: "white", border: "none", borderRadius: 8, fontSize: 16, cursor: "pointer", marginBottom: 24, fontFamily: theme.fontBody, fontWeight: "bold" },
    targetDateRow: { marginBottom: 16, padding: "12px 16px", backgroundColor: theme.bgPage, borderRadius: 8, border: `1px solid ${theme.bgCardBorder}` },
    targetDateLabel: { fontSize: 12, color: theme.colorTextSoft, margin: "0 0 6px 0", fontWeight: "bold", textTransform: "uppercase", fontFamily: theme.fontBody },
    targetDateValue: { fontSize: 15, color: theme.colorText, margin: 0, fontWeight: "500", fontFamily: theme.fontBody },
    dateEditRow: { display: "flex", alignItems: "center", gap: 8 },
    dateInput: { padding: "8px 12px", borderRadius: 8, border: `1px solid ${theme.bgCardBorder}`, fontSize: 14, backgroundColor: theme.bgPage, color: theme.colorText, fontFamily: theme.fontBody },
    editDateBtn: { padding: "6px 12px", backgroundColor: theme.bgPage, border: `1px solid ${theme.bgCardBorder}`, borderRadius: 6, cursor: "pointer", fontSize: 13, color: theme.colorText, fontFamily: theme.fontBody },
    saveDateBtn: { padding: "6px 12px", backgroundColor: theme.colorSuccess, color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13, fontFamily: theme.fontBody },
    cancelDateBtn: { padding: "6px 12px", backgroundColor: theme.bgPage, border: `1px solid ${theme.bgCardBorder}`, borderRadius: 6, cursor: "pointer", fontSize: 13, color: theme.colorText, fontFamily: theme.fontBody },
    // Quiz alert banner (Phase 3 — student only)
    quizAlertBanner: { backgroundColor: "#fff5f5", border: "2px solid #fc8181", borderRadius: 12, padding: "14px 18px", marginBottom: 16 },
    quizAlertText: { fontSize: 14, color: "#c53030", fontWeight: "bold", margin: "0 0 4px 0", fontFamily: theme.fontBody },
    // Collapsible section header (Phase 4)
    collapsibleHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", background: "none", border: "none", width: "100%", padding: 0, textAlign: "left" },
    chevron: { fontSize: 14, color: theme.colorTextSoft, marginLeft: 8, flexShrink: 0 },
    // Quiz row styles
    quizRowLocked: { display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", backgroundColor: theme.bgPage, borderRadius: 10, marginBottom: 10, opacity: 0.6, border: `1px solid ${theme.bgCardBorder}` },
    quizRowReady: { display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", backgroundColor: isChild ? theme.bgCard : "#ebf8ff", borderRadius: 10, marginBottom: 10, cursor: "pointer", border: `2px solid ${theme.colorPrimary}` },
    quizRowDone: { display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", backgroundColor: isChild ? theme.bgCard : "#f0fff4", borderRadius: 10, marginBottom: 10, border: `2px solid ${theme.colorSuccess}` },
    // Reviewed — same as done but clickable (parent finalized, student can see feedback)
    quizRowReviewed: { display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", backgroundColor: isChild ? theme.bgCard : "#f0fff4", borderRadius: 10, marginBottom: 10, border: `2px solid ${theme.colorSuccess}`, cursor: "pointer" },
    // Revise — red, clickable (parent sent back, student must act)
    quizRowRevise: { display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", backgroundColor: "#fff5f5", borderRadius: 10, marginBottom: 10, cursor: "pointer", border: "2px solid #fc8181" },
    quizRowIcon: { fontSize: 22, margin: 0 },
    quizRowText: { flex: 1 },
    quizRowTitle: { fontSize: 15, fontWeight: "bold", color: theme.colorTextSoft, margin: "0 0 2px 0", fontFamily: theme.fontBody },
    quizRowTitleReady: { fontSize: 15, fontWeight: "bold", color: theme.colorPrimary, margin: "0 0 2px 0", fontFamily: theme.fontBody },
    quizRowTitleRevise: { fontSize: 15, fontWeight: "bold", color: "#c53030", margin: "0 0 2px 0", fontFamily: theme.fontBody },
    quizRowSub: { fontSize: 13, color: theme.colorTextSoft, margin: 0, fontFamily: theme.fontBody },
    quizRowSubReady: { fontSize: 13, color: theme.colorPrimary, margin: 0, fontFamily: theme.fontBody },
    quizRowSubRevise: { fontSize: 13, color: "#e53e3e", margin: 0, fontWeight: "bold", fontFamily: theme.fontBody },
    quizArrow: { fontSize: 18, color: theme.colorPrimary, margin: 0 },
  };

  return (
    <div style={styles.container}>
      <Header />

      {/* Sidekick Banner — children only */}
      {isChild && (
        <div style={{ padding: "16px 24px 0", maxWidth: 600, margin: "0 auto" }}>
          <SidekickBanner />
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div style={styles.modalOverlay} onClick={() => setShowDeleteModal(false)}>
          <div style={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Remove this book?</h3>
            <p style={styles.modalText}>
              This will permanently delete <strong>{book.title}</strong> along with all reading sessions and notes. This cannot be undone.
            </p>
            <div style={styles.modalButtons}>
              <button style={styles.modalCancel} onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button style={styles.modalConfirm} onClick={deleteBook}>
                Remove Book
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={styles.content}>
        {/* Quiz alert banner — student only, shown when any quiz needs attention */}
        {!isParent && (reviseAlertCount > 0 || newFeedbackCount > 0) && (
          <div style={styles.quizAlertBanner}>
            {reviseAlertCount > 0 && (
              <p style={styles.quizAlertText}>
                🔁 {reviseAlertCount} quiz{reviseAlertCount > 1 ? "zes" : ""} sent back for revision — see below
              </p>
            )}
            {newFeedbackCount > 0 && (
              <p style={{ ...styles.quizAlertText, marginBottom: 0 }}>
                🏆 {newFeedbackCount} quiz{newFeedbackCount > 1 ? "zes have" : " has"} new feedback ready — see below
              </p>
            )}
          </div>
        )}

        {/* Book Info */}
        <div style={styles.card}>
          <div style={styles.bookHeader}>
            {book.coverUrl && (
              <img src={book.coverUrl} alt={book.title} style={styles.cover} />
            )}
            <div style={styles.bookMeta}>
              <h2 style={styles.bookTitle}>{book.title}</h2>
              <p style={styles.bookAuthor}>by {book.author}</p>
              {book.publisher && <p style={styles.bookMetaText}>📚 {book.publisher}</p>}
              {book.year && <p style={styles.bookMetaText}>📅 Published {book.year}</p>}
            </div>
            {canDelete && (
              <button style={styles.closeBtn} onClick={() => setShowDeleteModal(true)} title="Remove book">
                ✕
              </button>
            )}
          </div>
          {childBlocked && (
            <p style={styles.askParentText}>💬 Need to remove this book? Ask a parent for help.</p>
          )}
          <button style={styles.editDetailsBtn} onClick={() => navigate(`/book/${id}/edit`)}>
            ✏️ Edit Book Details
          </button>
        </div>

        {/* Progress Stats */}
        <div style={styles.card}>
          <button style={styles.collapsibleHeader} onClick={() => toggleSection('progress')}>
            <h3 style={{ ...styles.sectionTitle, marginBottom: 0 }}>Reading Progress</h3>
            <span style={styles.chevron}>{openSections.progress ? '▲' : '▼'}</span>
          </button>
          {openSections.progress && (<div style={{ marginTop: 16 }}>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: `${stats.percentComplete}%` }} />
          </div>
          <p style={styles.progressText}>{stats.percentComplete}% complete</p>

          <div style={styles.statsGrid}>
            <div style={styles.statBox}>
              <p style={styles.statNumber}>{stats.pagesRead}</p>
              <p style={styles.statLabel}>Pages Read</p>
            </div>
            <div style={styles.statBox}>
              <p style={styles.statNumber}>{stats.remaining}</p>
              <p style={styles.statLabel}>Pages Left</p>
            </div>
            <div style={styles.statBox}>
              <p style={styles.statNumber}>{book.currentPage}</p>
              <p style={styles.statLabel}>Current Page</p>
            </div>
            <div style={styles.statBox}>
              <p style={styles.statNumber}>{book.totalPages}</p>
              <p style={styles.statLabel}>Total Pages</p>
            </div>
          </div>

          {/* Target Date Editor */}
          <div style={styles.targetDateRow}>
            <div>
              <p style={styles.targetDateLabel}>🎯 Target Finish Date</p>
              {isParent && editingDate ? (
                <div style={styles.dateEditRow}>
                  <input
                    style={styles.dateInput}
                    type="date"
                    value={newTargetDate}
                    onChange={(e) => setNewTargetDate(e.target.value)}
                  />
                  <button style={styles.saveDateBtn} onClick={saveTargetDate}>Save</button>
                  <button style={styles.cancelDateBtn} onClick={() => setEditingDate(false)}>Cancel</button>
                </div>
              ) : (
                <div style={styles.dateEditRow}>
                  <p style={styles.targetDateValue}>
                    {book.targetDate ? new Date(book.targetDate).toLocaleDateString() : "No date set"}
                  </p>
                  {isParent && (
                    <button style={styles.editDateBtn} onClick={() => {
                      setNewTargetDate(book.targetDate || "");
                      setEditingDate(true);
                    }}>✏️ Edit</button>
                  )}
                </div>
              )}
            </div>
          </div>

          {stats.pagesPerDay && (
            <div style={styles.recastBanner}>
              <p style={styles.recastText}>📖 Read {stats.pagesPerDay} pages/day to finish on time</p>
              {stats.daysLeft && <p style={styles.recastSub}>{stats.daysLeft} days remaining until your goal date</p>}
            </div>
          )}

          {stats.projectedDate && (
            <div style={{ ...styles.recastBanner, backgroundColor: "#4299e1" }}>
              <p style={styles.recastText}>📅 At your current pace, you'll finish by {stats.projectedDate.toLocaleDateString()}</p>
              {stats.avgPages && <p style={styles.recastSub}>Averaging {stats.avgPages} pages per session</p>}
            </div>
          )}

          {!stats.pagesPerDay && !stats.projectedDate && stats.remaining > 0 && (
            <div style={{ ...styles.recastBanner, backgroundColor: "#a0aec0" }}>
              <p style={styles.recastText}>📚 Set a target date to see your daily reading goal!</p>
            </div>
          )}
          </div>)}
        </div>

        {/* Queue prompt — only shown when book is in queue */}
        {isQueue && (
          <div style={styles.card}>
            <h3 style={styles.sectionTitle}>📚 Ready to Start?</h3>
            <p style={{ fontSize: 15, color: theme.colorTextSoft, marginBottom: 16, fontFamily: theme.fontBody }}>
              This book is in your queue. Set an optional target date and tap Start Reading when you're ready!
            </p>
            <label style={styles.label}>🎯 Target Finish Date (optional)</label>
            <input
              style={styles.dateInput}
              type="date"
              value={newTargetDate}
              onChange={(e) => setNewTargetDate(e.target.value)}
            />
            <button
              style={{ ...styles.logBtn, marginTop: 12 }}
              onClick={promoteToActive}
            >
              📖 Start Reading!
            </button>
          </div>
        )}

        {/* Log Session */}
        {!isQueue && (
        <div style={styles.card}>
          <button style={styles.collapsibleHeader} onClick={() => toggleSection('logSession')}>
            <h3 style={{ ...styles.sectionTitle, marginBottom: 0 }}>Log Reading Session</h3>
            <span style={styles.chevron}>{openSections.logSession ? '▲' : '▼'}</span>
          </button>
          {openSections.logSession && (<div style={{ marginTop: 16 }}>
          <p style={styles.currentPageText}>You left off on page <strong>{book.currentPage}</strong></p>
          <label style={styles.label}>What page did you finish on?</label>
          <input
            style={styles.input}
            type="number"
            placeholder={`Enter page number (${book.currentPage + 1} - ${book.totalPages})`}
            value={endPage}
            onChange={(e) => setEndPage(e.target.value)}
          />
          <button style={styles.logBtn} onClick={logSession} disabled={loading}>
            {loading ? "Saving..." : "Log Session"}
          </button>
          </div>)}
        </div>
        )}

        {/* Session History */}
        {book.sessions && book.sessions.length > 0 && (
          <div style={styles.card}>
            <button style={styles.collapsibleHeader} onClick={() => toggleSection('sessionHistory')}>
              <h3 style={{ ...styles.sectionTitle, marginBottom: 0 }}>Session History</h3>
              <span style={styles.chevron}>{openSections.sessionHistory ? '▲' : '▼'}</span>
            </button>
            {openSections.sessionHistory && (<div style={{ marginTop: 16 }}>
            {[...book.sessions].reverse().map((session, i) => (
              <div key={i} style={styles.sessionRow}>
                <div>
                  <p style={styles.sessionDate}>{new Date(session.date).toLocaleDateString()}</p>
                  <p style={styles.sessionPages}>Pages {session.startPage} → {session.endPage}</p>
                </div>
                <p style={styles.sessionCount}>+{session.pagesRead} pages</p>
              </div>
            ))}
            </div>)}
          </div>
        )}

        {/* Quizzes */}
        {quizzes.length > 0 && (
          <div style={styles.card}>
            <button style={styles.collapsibleHeader} onClick={() => toggleSection('quizzes')}>
              <h3 style={{ ...styles.sectionTitle, marginBottom: 0 }}>Reading Quizzes</h3>
              <span style={styles.chevron}>{openSections.quizzes ? '▲' : '▼'}</span>
            </button>
            {openSections.quizzes && (<div style={{ marginTop: 16 }}>
            {quizzes.map((quiz) => {
              const status = getQuizStatus(quiz);

              if (status.state === "upcoming") {
                return (
                  <div key={quiz.id} style={styles.quizRowLocked}>
                    <p style={styles.quizRowIcon}>📋</p>
                    <div style={styles.quizRowText}>
                      <p style={styles.quizRowTitle}>{quiz.title}</p>
                      <p style={styles.quizRowSub}>Unlocks at page {quiz.milestone} — keep reading!</p>
                    </div>
                  </div>
                );
              }

              if (status.state === "pending") {
                return (
                  <div key={quiz.id} style={styles.quizRowLocked}>
                    <p style={styles.quizRowIcon}>⏳</p>
                    <div style={styles.quizRowText}>
                      <p style={styles.quizRowTitle}>{quiz.title}</p>
                      <p style={styles.quizRowSub}>Waiting for parent to unlock</p>
                    </div>
                  </div>
                );
              }

              if (status.state === "unlocked") {
                return (
                  <div key={quiz.id} style={styles.quizRowReady}
                    onClick={() => navigate(`/book/${id}/quiz/${quiz.id}`)}>
                    <p style={styles.quizRowIcon}>🎯</p>
                    <div style={styles.quizRowText}>
                      <p style={styles.quizRowTitleReady}>{quiz.title}</p>
                      <p style={styles.quizRowSubReady}>Ready to take — tap to begin!</p>
                    </div>
                    <p style={styles.quizArrow}>→</p>
                  </div>
                );
              }

              // Sent back for revision — clickable, routes back into the quiz
              // so the student can see their old answers, the parent's feedback,
              // and revise before resubmitting.
              if (status.state === "revise") {
                return (
                  <div key={quiz.id} style={styles.quizRowRevise}
                    onClick={() => navigate(`/book/${id}/quiz/${quiz.id}`)}>
                    <p style={styles.quizRowIcon}>🔁</p>
                    <div style={styles.quizRowText}>
                      <p style={styles.quizRowTitleRevise}>{quiz.title}</p>
                      <p style={styles.quizRowSubRevise}>Your parent left feedback — tap to revise and resubmit</p>
                    </div>
                    <p style={{ ...styles.quizArrow, color: "#c53030" }}>→</p>
                  </div>
                );
              }

              // Awaiting parent review — non-clickable, nothing to do yet
              if (status.state === "completed") {
                return (
                  <div key={quiz.id} style={styles.quizRowDone}>
                    <p style={styles.quizRowIcon}>✅</p>
                    <div style={styles.quizRowText}>
                      <p style={styles.quizRowTitle}>{quiz.title}</p>
                      <p style={styles.quizRowSub}>Submitted — awaiting parent review</p>
                    </div>
                  </div>
                );
              }

              // Parent finalized — clickable so student can view results and feedback
              if (status.state === "reviewed") {
                return (
                  <div key={quiz.id} style={styles.quizRowReviewed}
                    onClick={() => navigate(`/book/${id}/quiz/${quiz.id}`)}>
                    <p style={styles.quizRowIcon}>🏆</p>
                    <div style={styles.quizRowText}>
                      <p style={styles.quizRowTitle}>{quiz.title}</p>
                      <p style={styles.quizRowSub}>Final score: {status.earned} / {status.possible} — tap to see feedback</p>
                    </div>
                    <p style={styles.quizArrow}>→</p>
                  </div>
                );
              }

              return null;
            })}
            </div>)}
          </div>
        )}

        {/* Outline Button */}
        <button style={styles.scaffoldBtn} onClick={() => navigate(`/book/${id}/scaffolding`)}>
          📝 Book Outline & Notes
        </button>
{/* Book Report Button — only shown when book is finished */}
        {book.pagesRead >= book.totalPages && (
          <button
            style={{ ...styles.scaffoldBtn, backgroundColor: "#2f855a", marginTop: 0 }}
            onClick={openBookReport}
          >
            📄 Book Report
          </button>
        )}
      </div>
    </div>
  );
}